import Header from '@/components/layout/Header';
import heroBackground from '@/assets/hero-background.jpg?format=webp&quality=72';
import Footer from '@/components/layout/Footer';
import { Reveal } from '@/components/Reveal';
import LoanDetails from '@/components/home/LoanDetails';
import LoanCalculator from '@/components/home/LoanCalculator';
import EligibilitySecurity from '@/components/home/EligibilitySecurity';
import CompanyInfo from '@/components/home/CompanyInfo';

// Preload LCP hero image so the browser discovers it from the initial document
// and fetches it with high priority, improving LCP discovery.
if (typeof document !== 'undefined' && !document.querySelector('link[data-hero-preload]')) {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = heroBackground;
  link.setAttribute('fetchpriority', 'high');
  link.setAttribute('data-hero-preload', 'true');
  document.head.appendChild(link);
}
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Link } from '@/lib/router-compat';
import { 
  DollarSign, 
  Clock, 
  CheckCircle2, 
  ArrowRight, 
  Zap, 
  Lock, 
  TrendingUp,
  Users,
  FileText,
  CreditCard,
  Home,
  Car,
  Wallet,
  BadgePercent,
  Phone,
  Mail,
  MapPin,
  Send,
  ChevronDown,
  Award,
  Building2,
  Banknote
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function Index() {
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsSubmitting(false);
    toast({ title: 'Message sent!', description: "We'll get back to you within 24 hours." });
    setContactForm({ name: '', email: '', message: '' });
  };

  const loanOptions = [
    { 
      icon: CreditCard, 
      title: 'Debt Paydown Loan', 
      description: 'Pay off credit cards or personal loan balances faster.',
    },
    { 
      icon: Wallet, 
      title: 'Cash Loan', 
      description: 'Cover a major purchase, life event, or unexpected expense.',
    },
    { 
      icon: Home, 
      title: 'Home Improvement', 
      description: 'Renovate, repair, or upgrade your property.',
    },
    { 
      icon: Car, 
      title: 'Auto Refinance', 
      description: 'Lower your rate and keep more of what you earn.',
    },
  ];

  const testimonials = [
    { name: 'Sarah M.', type: 'Personal Loan', text: 'The application process was incredibly easy. I got approved within hours and the funds were in my account the next day!' },
    { name: 'Michael T.', type: 'Home Improvement', text: 'Best rates I found anywhere. The whole team was professional and made me feel confident throughout the process.' },
    { name: 'Jennifer L.', type: 'Debt Consolidation', text: 'Finally consolidated all my debt into one manageable payment. Quick Credit made it simple and saved me money!' },
  ];

  const faqs = [
    {
      question: "Will checking my rate hurt my credit score?",
      answer: "No! When you check your rate with Quick Credit, we perform a soft credit inquiry that does not impact your credit score. A hard inquiry only occurs if you decide to proceed with a loan application."
    },
    {
      question: "How quickly can I get my funds?",
      answer: "Once approved, most customers receive their funds within 24-48 hours via direct deposit to their bank account. Some loans may fund same-day depending on your bank."
    },
    {
      question: "What credit score do I need to qualify?",
      answer: "We consider a wide range of credit profiles. While there's no strict minimum, most approved borrowers have a credit score of 600 or higher. We look at your complete financial picture."
    },
    {
      question: "Are there any hidden fees?",
      answer: "Absolutely not. We believe in complete transparency. You'll see all fees upfront before you accept any loan offer, including origination fees if applicable. No prepayment penalties ever."
    },
    {
      question: "Can I pay off my loan early?",
      answer: "Yes! There are no prepayment penalties. You can pay off your loan early at any time without any additional fees, which can save you money on interest."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section - LendingClub Style */}
      <section className="pt-24 pb-12 relative">
        {/* Image hero band */}
        <div
          className="absolute inset-x-0 top-0 h-[44vh] md:h-[52vh] bg-cover bg-center"
          style={{ backgroundImage: `url(${heroBackground})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-primary/70 via-primary/50 to-background" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          {/* Main Headline */}
          <div className="max-w-3xl mx-auto text-center pt-6 pb-10 animate-slide-up">
            <h1 className="font-display text-3xl md:text-5xl font-bold text-primary-foreground mb-4 leading-tight">
              Personal loan rates starting as low as{' '}
              <span className="text-accent">5.99% APR</span>
            </h1>
            <p className="text-base md:text-lg text-primary-foreground/85 max-w-xl mx-auto">
              Check your rate in minutes. It won't impact your credit score.
            </p>
          </div>

          {/* Loan Selection - Stacked List Cards (LendingClub style) */}
          <div className="max-w-2xl mx-auto space-y-3">
            {loanOptions.map((option, index) => (
              <Link
                to="/apply"
                key={index}
                className="group block animate-slide-up"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <Card className="bg-card border border-border/60 shadow-md hover:shadow-xl hover:border-accent/40 transition-all duration-200">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                      <option.icon className="h-6 w-6 text-accent" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display font-semibold text-foreground text-base mb-0.5">
                        {option.title}
                      </h3>
                      <p className="text-xs md:text-sm text-muted-foreground line-clamp-2">
                        {option.description}
                      </p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                  </CardContent>
                </Card>
              </Link>
            ))}

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 pt-6">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Lock className="h-4 w-4 text-accent" />
                <span className="text-xs md:text-sm">Bank-level encryption</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-accent" />
                <span className="text-xs md:text-sm">No credit score impact</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <DollarSign className="h-4 w-4 text-accent" />
                <span className="text-xs md:text-sm">No hidden fees</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Banner */}
      <section className="py-10 bg-card border-b border-border">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
            <Reveal direction="up" delay={0} className="text-center">
              <div className="text-3xl md:text-4xl font-display font-bold text-foreground mb-1">5M+</div>
              <div className="text-muted-foreground text-sm">Members Helped</div>
            </Reveal>
            <div className="hidden md:block w-px h-12 bg-border" />
            <Reveal direction="up" delay={100} className="text-center">
              <div className="text-3xl md:text-4xl font-display font-bold text-foreground mb-1">$80B+</div>
              <div className="text-muted-foreground text-sm">Loans Funded</div>
            </Reveal>
            <div className="hidden md:block w-px h-12 bg-border" />
            <Reveal direction="up" delay={200} className="text-center flex flex-col items-center">
              <div className="flex items-center gap-1 mb-1">
                <span className="text-3xl md:text-4xl font-display font-bold text-foreground">4.9</span>
                <div className="flex text-warning">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
              </div>
              <div className="text-muted-foreground text-sm">Trustpilot Rating</div>
            </Reveal>
            <div className="hidden md:block w-px h-12 bg-border" />
            <Reveal direction="up" delay={300} className="flex items-center gap-3">
              <Award className="h-10 w-10 text-accent" />
              <div>
                <div className="font-semibold text-foreground text-sm">Best Online Lender</div>
                <div className="text-muted-foreground text-xs">Forbes 2024</div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* How It Works - Visual Steps */}
      <section id="how-it-works" className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <Reveal direction="up" className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
              How a personal loan with Quick Credit works
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Get the funds you need in three simple steps
            </p>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Reveal direction="up" delay={0} className="relative">
              <Card className="border-0 shadow-lg h-full">
                <CardContent className="p-8 text-center">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold text-sm">
                    1
                  </div>
                  <div className="w-20 h-20 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-6 mt-2">
                    <FileText className="h-10 w-10 text-accent" />
                  </div>
                  <h3 className="font-display text-xl font-semibold mb-3">Check your rate</h3>
                  <p className="text-muted-foreground text-sm">
                    Answer a few questions to see if you pre-qualify. It only takes minutes and won't affect your credit score.
                  </p>
                </CardContent>
              </Card>
              <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-accent/30" />
            </Reveal>

            <Reveal direction="up" delay={150} className="relative">
              <Card className="border-0 shadow-lg h-full">
                <CardContent className="p-8 text-center">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold text-sm">
                    2
                  </div>
                  <div className="w-20 h-20 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-6 mt-2">
                    <BadgePercent className="h-10 w-10 text-accent" />
                  </div>
                  <h3 className="font-display text-xl font-semibold mb-3">Choose your offer</h3>
                  <p className="text-muted-foreground text-sm">
                    Review multiple personalized offers and select the loan amount, term, and monthly payment that works for you.
                  </p>
                </CardContent>
              </Card>
              <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-accent/30" />
            </Reveal>

            <Reveal direction="up" delay={300} className="relative">
              <Card className="border-0 shadow-lg h-full">
                <CardContent className="p-8 text-center">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold text-sm">
                    3
                  </div>
                  <div className="w-20 h-20 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-6 mt-2">
                    <Banknote className="h-10 w-10 text-accent" />
                  </div>
                  <h3 className="font-display text-xl font-semibold mb-3">Get your money</h3>
                  <p className="text-muted-foreground text-sm">
                    Complete your application and receive funds as soon as the next business day via direct deposit.
                  </p>
                </CardContent>
              </Card>
            </Reveal>
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-lg px-10">
              <Link to="/apply">
                Check Your Rate <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal direction="right">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-6">
                We're rewriting the rules of traditional lending
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                We only win when our customers succeed. That's why we've helped over 5 million members reach their financial goals, and we're just getting started.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-6 w-6 text-success" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Competitive Rates</h3>
                    <p className="text-muted-foreground text-sm">Fixed rates from 8.98% - 35.99% APR with AutoPay</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Zap className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Fast Funding</h3>
                    <p className="text-muted-foreground text-sm">Get your funds as soon as the next business day</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-warning/10 flex items-center justify-center flex-shrink-0">
                    <Lock className="h-6 w-6 text-warning" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">No Prepayment Penalties</h3>
                    <p className="text-muted-foreground text-sm">Pay off your loan early without any extra fees</p>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal direction="left" delay={150} className="grid grid-cols-2 gap-4">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-display font-bold text-accent mb-2">$60K</div>
                  <div className="text-sm text-muted-foreground">Max loan amount</div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-display font-bold text-accent mb-2">24hr</div>
                  <div className="text-sm text-muted-foreground">Funding speed</div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-display font-bold text-accent mb-2">$0</div>
                  <div className="text-sm text-muted-foreground">Hidden fees</div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-display font-bold text-accent mb-2">5min</div>
                  <div className="text-sm text-muted-foreground">Application time</div>
                </CardContent>
              </Card>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6">
          <Reveal direction="up" className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              Trusted by millions of members
            </h2>
            <p className="text-primary-foreground/70 text-lg max-w-2xl mx-auto">
              See what our customers have to say about their experience
            </p>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <Reveal key={index} direction="up" delay={index * 120}>
                <Card className="border-0 shadow-xl h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-1 mb-4 text-warning">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                          <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                        </svg>
                      ))}
                    </div>
                    <p className="text-foreground mb-6">"{testimonial.text}"</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                        <Users className="h-5 w-5 text-accent" />
                      </div>
                      <div>
                        <div className="font-semibold text-sm text-foreground">{testimonial.name}</div>
                        <div className="text-muted-foreground text-xs">{testimonial.type}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <LoanDetails />
      <LoanCalculator />
      <EligibilitySecurity />
      <CompanyInfo />

      {/* FAQ Section */}
      <section id="faq" className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
                Questions? We've got answers.
              </h2>
              <p className="text-muted-foreground text-lg">
                Everything you need to know about getting a loan with Quick Credit
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="border border-border rounded-xl px-6 data-[state=open]:shadow-lg transition-shadow"
                >
                  <AccordionTrigger className="text-left font-semibold hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-accent">
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-accent-foreground mb-4">
            Ready to get started?
          </h2>
          <p className="text-accent-foreground text-lg max-w-2xl mx-auto mb-8">
            Check your rate in minutes with no impact to your credit score. 
            Join over 5 million members who trust Quick Credit.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="secondary" className="text-lg px-10 bg-card text-foreground hover:bg-card/90">
              <Link to="/apply">
                Check Your Rate <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg px-10 bg-accent-foreground text-accent border-accent-foreground hover:bg-accent-foreground/90">
              <Link to="/contact">
                Contact Us
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Trust Footer - Badges & Disclosures */}
      <section className="py-12 bg-card border-t border-border">
        <div className="container mx-auto px-6 max-w-3xl">
          {/* Badges */}
          <div className="flex flex-col items-center gap-6 mb-10">
            {/* Equal Housing Lender */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 border-2 border-foreground rounded-sm flex items-center justify-center">
                <Home className="h-6 w-6 text-foreground" strokeWidth={2.5} />
              </div>
              <div className="font-display font-bold text-foreground tracking-wide leading-tight">
                <div className="text-xs">EQUAL HOUSING</div>
                <div className="text-base">LENDER</div>
              </div>
            </div>

            {/* BBB Accredited */}
            <div className="flex items-center bg-foreground rounded overflow-hidden">
              <div className="px-3 py-2 text-background font-display font-bold italic text-lg border-r border-background/20">
                BBB
              </div>
              <div className="px-3 py-2 text-background text-[10px] font-bold leading-tight tracking-wide">
                <div>ACCREDITED</div>
                <div>BUSINESS</div>
              </div>
            </div>

            {/* VeriSign Secured */}
            <div className="flex items-center gap-2 border-2 border-foreground rounded-full px-4 py-2">
              <div className="w-7 h-7 rounded-full border-2 border-foreground flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-foreground" strokeWidth={3} />
              </div>
              <div className="font-display font-bold text-foreground leading-tight">
                <div className="text-sm">VeriSign</div>
                <div className="text-xs">Secured</div>
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-8 space-y-6 text-sm text-muted-foreground leading-relaxed">
            <p>
              Checking, savings and CD accounts subject to approval, which may include credit approval. No minimum opening deposit required for LevelUp Checking or LevelUp Savings. $500 minimum opening deposit required for CDs. Please see Product Terms & Conditions for additional product details. Visit our Help Center for additional information.
            </p>

            <ol className="list-decimal pl-5 space-y-4">
              <li>
                For Personal Loans, APR ranges from 5.96% APR to 35.99% APR and origination/processing fee ranges from 0.00% to 8.00% of the loan amount. APRs and origination/processing fees are determined at the time of application. The lowest APR may be available to borrowers with excellent credit, subject to additional factors including, but not limited to, loan amount, loan term, and sufficient investor commitment. Advertised rates and fees are valid as of April 27, 2026, are subject to change without notice, and/or through all application channels or platforms.
              </li>
              <li>
                Savings are not guaranteed and depend upon various factors, including but not limited to interest rates, fees, term length, and making payments as agreed.
              </li>
              <li>
                Checking a rate through us generates a soft inquiry on a person's credit report, which does not impact that person's credit score. A hard credit inquiry, which may affect that person's credit score, only appears on the person's credit report if and when a loan is issued to the person.
              </li>
              <li>
                The APR discounted rate is a discount that some customers may receive for taking out a loan to pay down existing qualifying debt paid directly by Quick Credit; such rate is discounted from the rate given for taking a full cash loan. Not all applicants will qualify for the discount. Any actual discount rate will be determined at the time of application. The best APR discounts may be available to borrowers with excellent credit. Advertised discounted rates are subject to change without notice.
              </li>
              <li>
                Standard data and message rates may apply to Mobile Banking services. This includes the use of Quick Credit Mobile, Quick Credit Commercial, and other services. Android is a trademark of Google LLC. iPhone and iPad are trademarks of Apple Inc., registered in the U.S. and other countries.
              </li>
            </ol>
          </div>
        </div>
      </section>

      {/* Full Legal Disclosures */}
      <section className="py-12 bg-background border-t border-border">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="space-y-6 text-xs text-muted-foreground leading-relaxed">
            <p>
              Personal loans are available through our affiliate Achieve Personal Loans (NMLS ID #227977), originated by Cross River Bank, a New Jersey State Chartered Commercial Bank or Pathward®, N.A., Equal Housing Lenders and may not be available in all states. All loan and rate terms are subject to eligibility restrictions, application review, credit score, loan amount, loan term, lender approval, credit usage and history. Loan amounts generally range from $5,000 to $50,000, vary by state and are offered based on meeting underwriting conditions and loan purpose. APRs range from 8.99 to 35.99% and include applicable origination fees. Repayment periods range from 24 to 60 months. Example loan: four-year $20,000 loan with an origination fee of 6.99%, a rate of 15.49% and corresponding APR of 19.54%, would have an estimated monthly payment of $561.60 and a total cost of $26,956.80. To qualify for a 8.99% APR loan, a borrower will need excellent credit, a loan amount less than $12,000.00, and a term of 24 months. Loan origination fees vary from 1.99% to 6.99%. The origination fee is deducted from the loan proceeds. Adding a co-borrower with sufficient income; using at least eighty-five percent (85%) of the loan proceeds to pay off qualifying existing debt directly; or showing proof of sufficient retirement savings, could help you also qualify for lower rates. Funding time periods are estimates and can vary for each loan request. Same day decisions assume a completed application with all required supporting documentation submitted early enough on a day that our offices are open. Achieve Personal Loans loan consultants' hours are Monday-Friday 6am-8pm AZ time, and Saturday-Sunday 7am-4pm Az time. Loan payments can be made anytime through the customer dashboard or with our servicing agents, who are available 6am-6pm M-F AZ time. Sat 7am-11am AZ time. All estimates for Achieve Debt Relief's services are based on prior results, which will vary depending on your specific enrolled creditors and your individual program terms. Not all Achieve Debt Relief clients are able to complete their program for various reasons, including their ability to save sufficient funds. Achieve Debt Relief does not assume your debts, make monthly payments to creditors or provide tax, bankruptcy, accounting or legal advice or credit repair services. Achieve Debt Relief's services are not available in all states, including New Jersey, and their fees may vary from state to state. Please contact a tax professional to discuss potential tax consequences of less than full balance debt relief. Read and understand all program materials prior to enrollment. The use of Achieve Debt Relief services will likely adversely affect your creditworthiness, may result in you being subject to collections or being sued by creditors or collectors and may increase the outstanding balances of your enrolled accounts due to the accrual of fees and interest. However, negotiated settlements Achieve Debt Relief obtained on your behalf resolve the entire account, including all accrued fees and interest. C.P.D. Reg. No. T.S.12-O3825.
            </p>

            <p>
              Freedom Financial Network, LLC, offers various financial products and services through its wholly owned subsidiary, FDR, and affiliates (collectively, "Freedom"), which may be provided by Freedom or other unaffiliated third parties. All estimates for FDR's services are based on prior results, which will vary depending on your specific enrolled creditors and your individual program terms. Not all clients are able to complete their program for various reasons, including their ability to save sufficient funds. FDR does not guarantee that your debts will be resolved for a specific amount or percentage or within a specific period of time. Nor will it assume your debts, make monthly payments to creditors or provide tax, accounting, legal advise or services. FDR's service is not available in all states, including New Jersey, and its fees may vary from state to state. Please contact a tax professional to discuss potential tax consequences of less than full balance debt relief. Read and understand all program materials prior to enrollment. The use of FDR's services will likely adversely affect your creditworthiness, may result in you being subject to collections or being sued by creditors or collectors and may increase the outstanding balances of your enrolled accounts due to the accrual of fees and interest. However, negotiated results FDR obtains on your behalf resolve the entire account, including all accrued fees and interest. C.P.D. Reg. No. T.S.12-O3825. By proceeding, you acknowledge that you may be contacted by one or more representatives of Freedom to discuss options.
            </p>

            <p>
              Statistics reflect the results of the clients we and our affiliates have served since 2002 (as of April 2026).
            </p>

            <p className="text-foreground font-medium">
              © {new Date().getFullYear()} Quick Credit. All rights reserved.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
